<script lang="ts" setup>
import { reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import {
  ValidationResult,
  ImportDomainsRequest,
  AccountDetails,
  StripeCustomer,
  type ValidationResult as ValidationType,
  type ImportDomainsResponse as ImportDomainsResponseType,
  type ImportAccountDetails as ImportAccountDetailsType,
  type StripeCustomer as StripeCustomerType,
  type PaginatedResponse
} from '@/types'
import MigrationAccountForm from './MigrationAccountForm.vue'

const props = defineProps<{
  visible: boolean
  domains: ValidationType[]
}>()

const emit = defineEmits<{
  close: []
  'import-complete': [results: ImportDomainsResponseType]
}>()

const { t } = useI18n()
const notifyStore = useNotifyStore()

const data = reactive({
  // Account mode: 'existing' or 'new'
  accountMode: 'existing' as 'existing' | 'new',
  // Account selection
  selectedAccount: null as AccountDetails | null,
  allAccounts: [] as AccountDetails[],
  loadingAccounts: false,
  accountComboboxOpen: false,
  accountComboboxQuery: '',
  // New account form data
  newAccountData: null as ImportAccountDetailsType | null,
  // Sub-account options
  enableSubAccount: false,
  parentAccount: null as AccountDetails | null,
  parentComboboxOpen: false,
  parentComboboxQuery: '',
  relationshipType: 'reseller' as 'team' | 'reseller',
  billingMode: 'shared' as 'shared' | 'independent',
  // Stripe customer linking
  stripeSearch: '',
  stripeSearchResults: [] as StripeCustomerType[],
  searchingStripe: false,
  selectedStripeCustomer: null as StripeCustomerType | null,
  // Validation state
  validating: false,
  validated: false,
  validationResults: [] as ValidationType[],
  allValid: false,
  // Import state
  importing: false,
  importProgress: {
    current: 0,
    total: 0,
    currentDomain: '',
    completed: [] as { domain: string; success: boolean }[]
  }
})

// Load all accounts for combobox
const loadAllAccounts = async () => {
  data.loadingAccounts = true
  try {
    const res = await useAxios<null, PaginatedResponse<AccountDetails>>('GET', '/account/all?limit=200', true)
    if (res && res.status === STATUS_CODES.OK && res.data?.data) {
      // Use raw data - API is trusted, avoid overly strict Zod validation
      data.allAccounts = res.data.data as AccountDetails[]
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'MigrationImportPanel:loadAllAccounts' })
  } finally {
    data.loadingAccounts = false
  }
}

// Reset state when panel opens/closes
watch(() => props.visible, (visible) => {
  if (visible) {
    // Reset to initial state
    data.accountMode = 'existing'
    data.selectedAccount = null
    data.accountComboboxOpen = false
    data.accountComboboxQuery = ''
    data.newAccountData = null
    data.enableSubAccount = false
    data.parentAccount = null
    data.parentComboboxOpen = false
    data.parentComboboxQuery = ''
    data.relationshipType = 'reseller'
    data.billingMode = 'shared'
    data.stripeSearch = ''
    data.stripeSearchResults = []
    data.selectedStripeCustomer = null
    data.validated = false
    data.validationResults = []
    data.allValid = false
    data.importProgress = {
      current: 0,
      total: 0,
      currentDomain: '',
      completed: []
    }
    // Load accounts for combobox
    loadAllAccounts()
  }
})

// Filtered accounts for combobox
const filteredAccounts = computed(() => {
  if (!data.accountComboboxQuery.trim()) {
    return data.allAccounts
  }
  const query = data.accountComboboxQuery.toLowerCase().trim()
  return data.allAccounts.filter((acc) =>
    acc.email.toLowerCase().includes(query) ||
    (acc.name && acc.name.toLowerCase().includes(query)) ||
    (acc.surname && acc.surname.toLowerCase().includes(query)) ||
    (acc.org && acc.org.toLowerCase().includes(query))
  )
})

// Combobox handlers
const toggleCombobox = () => {
  data.accountComboboxOpen = !data.accountComboboxOpen
}

const handleComboboxBlur = () => {
  // Delay to allow click on option
  setTimeout(() => {
    data.accountComboboxOpen = false
  }, 150)
}

// Select an account from combobox
const selectAccount = (account: AccountDetails) => {
  data.selectedAccount = account
  data.accountComboboxQuery = ''
  data.accountComboboxOpen = false
  // Reset validation when account changes
  data.validated = false
  data.allValid = false
}

// Clear selected account
const clearSelectedAccount = () => {
  data.selectedAccount = null
  data.validated = false
  data.allValid = false
}

// Parent account combobox (sub-account feature)
const filteredParentAccounts = computed(() => {
  const exclude = data.selectedAccount?.id
  const base = data.allAccounts.filter((acc) => acc.id !== exclude)
  if (!data.parentComboboxQuery.trim()) return base.slice(0, 20)
  const q = data.parentComboboxQuery.toLowerCase().trim()
  return base.filter(
    (acc) =>
      acc.email.toLowerCase().includes(q) ||
      (acc.name && acc.name.toLowerCase().includes(q)) ||
      (acc.surname && acc.surname.toLowerCase().includes(q)) ||
      (acc.org && acc.org.toLowerCase().includes(q))
  )
})

const toggleParentCombobox = () => {
  data.parentComboboxOpen = !data.parentComboboxOpen
}

const handleParentComboboxBlur = () => {
  setTimeout(() => {
    data.parentComboboxOpen = false
  }, 150)
}

const selectParentAccount = (account: AccountDetails) => {
  data.parentAccount = account
  data.parentComboboxQuery = ''
  data.parentComboboxOpen = false
  data.validated = false
  data.allValid = false
}

const clearParentAccount = () => {
  data.parentAccount = null
  data.validated = false
  data.allValid = false
}

// Search for Stripe customers
const searchStripeCustomers = async () => {
  if (data.stripeSearch.trim().length < 2) {
    data.stripeSearchResults = []
    return
  }

  data.searchingStripe = true
  try {
    const query = data.stripeSearch.trim()
    // Search by email first, then by name
    const params = new URLSearchParams()
    if (query.includes('@')) {
      params.set('email', query)
    } else {
      params.set('name', query)
    }

    const res = await useAxios<null, StripeCustomerType[]>(
      'GET',
      `/admin/stripe/customers/search?${params.toString()}`,
      true
    )
    if (res && res.status === STATUS_CODES.OK) {
      const validate = StripeCustomer.array().safeParse(res.data)
      if (validate.success) {
        data.stripeSearchResults = validate.data
      }
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'MigrationImportPanel:searchStripeCustomers' })
  } finally {
    data.searchingStripe = false
  }
}

// Debounced Stripe search
let stripeSearchTimeout: ReturnType<typeof setTimeout> | null = null
watch(() => data.stripeSearch, () => {
  if (stripeSearchTimeout) clearTimeout(stripeSearchTimeout)
  stripeSearchTimeout = setTimeout(searchStripeCustomers, 300)
})

// Select a Stripe customer
const selectStripeCustomer = (customer: StripeCustomerType) => {
  data.selectedStripeCustomer = customer
  data.stripeSearch = ''
  data.stripeSearchResults = []
}

// Clear selected Stripe customer
const clearSelectedStripeCustomer = () => {
  data.selectedStripeCustomer = null
}

// Format Stripe customer creation date
const formatStripeDate = (timestamp: number) => {
  return new Date(timestamp * 1000).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

// Handle new account form data
const handleAccountFormChange = (formData: ImportAccountDetailsType) => {
  data.newAccountData = formData
  // Reset validation when form changes
  data.validated = false
  data.allValid = false
}

// Check if we can validate
const canValidate = computed(() => {
  if (props.domains.length === 0) return false
  if (data.accountMode === 'existing' && !data.selectedAccount) return false
  if (data.accountMode === 'new' && !data.newAccountData) return false
  return true
})

// Check if we can import
const canImport = computed(() => {
  return data.validated && data.allValid && canValidate.value
})

// Validate the import
const validateImport = async () => {
  if (!canValidate.value) return

  data.validating = true
  data.validationResults = []

  try {
    // Validate each domain using Promise.all for parallel validation
    const validationPromises = props.domains.map(async (domain) => {
      const res = await useAxios<{ domain: string }, ValidationType>(
        'POST',
        '/admin/import/validate',
        true,
        { domain: domain.domain }
      )
      if (res && res.status === STATUS_CODES.OK) {
        const validate = ValidationResult.safeParse(res.data)
        if (validate.success) {
          return validate.data
        }
      }
      return null
    })

    const validationResults = await Promise.all(validationPromises)
    const results = validationResults.filter((r): r is ValidationType => r !== null)

    data.validationResults = results
    data.allValid = results.every((r) => r.can_import)
    data.validated = true

    if (data.allValid) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.migration.validation.title'),
        message: t('dashboard.admin.migration.validation.allValid')
      })
    } else {
      const invalidCount = results.filter((r) => !r.can_import).length
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'warning',
        title: t('dashboard.admin.migration.validation.title'),
        message: t('dashboard.admin.migration.validation.someInvalid', { count: invalidCount })
      })
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'MigrationImportPanel:validateImport' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error)
    })
  } finally {
    data.validating = false
  }
}

// Execute the import
const executeImport = async () => {
  if (!canImport.value) return

  data.importing = true

  // Initialize progress tracking
  data.importProgress = {
    current: 0,
    total: props.domains.length,
    currentDomain: '',
    completed: []
  }

  try {
    // Build import request
    const imports = props.domains.map((domain) => {
      const req: {
        domain: string
        account_id?: number
        account?: ImportAccountDetailsType
        stripe_customer_id?: string
        parent_account_id?: number
        relationship_type?: 'team' | 'reseller'
        billing_mode?: 'shared' | 'independent'
      } = {
        domain: domain.domain
      }
      if (data.accountMode === 'existing' && data.selectedAccount?.id) {
        req.account_id = data.selectedAccount.id
      } else if (data.accountMode === 'new' && data.newAccountData) {
        req.account = data.newAccountData
      }
      // Add Stripe customer if selected
      if (data.selectedStripeCustomer?.id) {
        req.stripe_customer_id = data.selectedStripeCustomer.id
      }
      // Add sub-account relationship if enabled
      if (data.enableSubAccount && data.parentAccount?.id) {
        req.parent_account_id = data.parentAccount.id
        req.relationship_type = data.relationshipType as 'team' | 'reseller'
        req.billing_mode = data.billingMode as 'shared' | 'independent'
      }
      return req
    })

    const requestBody: ImportDomainsRequest = { imports }

    // Show we're starting the import
    data.importProgress.currentDomain = `${props.domains.length} Domains werden importiert...`

    const res = await useAxios<ImportDomainsRequest, ImportDomainsResponseType>(
      'POST',
      '/admin/import/domains',
      true,
      requestBody
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      // Use raw response - API is trusted
      const results = res.data as ImportDomainsResponseType

      // Update progress with results
      if (results.summary) {
        data.importProgress.current = results.summary.total
      }
      if (results.results) {
        data.importProgress.completed = results.results.map((r) => ({
          domain: r.domain,
          success: r.success
        }))
      }

      // Emit completion - parent closes drawer and shows notification
      emit('import-complete', results)
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'MigrationImportPanel:executeImport' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.admin.migration.notifications.importFailed'),
      message: getErrorMessage(error)
    })
  } finally {
    data.importing = false
  }
}

// Get display name for account
const getAccountDisplayName = (account: AccountDetails) => {
  if (account.org) return account.org
  if (account.name || account.surname) {
    return `${account.name || ''} ${account.surname || ''}`.trim()
  }
  return account.email
}
</script>

<template>
  <Teleport to="body">
    <Transition name="slide">
      <div v-if="visible" class="import-panel-overlay" @click.self="emit('close')">
        <div class="import-panel">
          <!-- Header -->
          <header class="panel-header">
            <div class="header-content">
              <h2 class="panel-title">
                {{ domains.length === 1
                  ? $t('dashboard.admin.migration.import.title')
                  : $t('dashboard.admin.migration.import.titleBatch')
                }}
              </h2>
              <p class="panel-subtitle">{{ $t('dashboard.admin.migration.import.subtitle') }}</p>
            </div>
            <button class="close-btn" @click="emit('close')">
              <v-icon icon="mdi-close" size="20" />
            </button>
          </header>

          <!-- Content -->
          <div class="panel-content">
            <!-- Selected Domains -->
            <section class="form-section">
              <h3 class="section-title">
                {{ $t('dashboard.admin.migration.import.selectedDomains') }}
              </h3>
              <div class="domain-chips">
                <span v-for="domain in domains" :key="domain.domain" class="domain-chip">
                  {{ domain.domain }}
                </span>
              </div>
            </section>

            <!-- Account Assignment -->
            <section class="form-section">
              <h3 class="section-title">
                {{ $t('dashboard.admin.migration.import.accountAssignment') }}
              </h3>

              <!-- Mode Toggle -->
              <div class="mode-toggle">
                <label class="mode-option" :class="{ active: data.accountMode === 'existing' }">
                  <input
                    v-model="data.accountMode"
                    type="radio"
                    value="existing"
                  />
                  <span class="option-label">
                    {{ $t('dashboard.admin.migration.import.existingAccount') }}
                  </span>
                </label>
                <label class="mode-option" :class="{ active: data.accountMode === 'new' }">
                  <input
                    v-model="data.accountMode"
                    type="radio"
                    value="new"
                  />
                  <span class="option-label">
                    {{ $t('dashboard.admin.migration.import.newAccount') }}
                  </span>
                </label>
              </div>

              <!-- Existing Account Selection -->
              <div v-if="data.accountMode === 'existing'" class="account-search-section">
                <!-- Selected Account Display -->
                <div v-if="data.selectedAccount" class="selected-account">
                  <div class="account-info">
                    <span class="account-name">{{ getAccountDisplayName(data.selectedAccount) }}</span>
                    <span class="account-email">{{ data.selectedAccount.email }}</span>
                  </div>
                  <button class="remove-account" @click="clearSelectedAccount">
                    <v-icon icon="mdi-close" size="16" />
                  </button>
                </div>

                <!-- Account Combobox -->
                <div v-else class="combobox-container">
                  <div
                    class="combobox-input-wrapper"
                    :class="{ focused: data.accountComboboxOpen }"
                  >
                    <v-icon icon="mdi-account-search" size="18" class="combobox-icon" />
                    <input
                      v-model="data.accountComboboxQuery"
                      type="text"
                      class="combobox-input"
                      placeholder="Account suchen oder auswählen..."
                      @focus="data.accountComboboxOpen = true"
                      @blur="handleComboboxBlur"
                    />
                    <v-progress-circular
                      v-if="data.loadingAccounts"
                      indeterminate
                      size="16"
                      width="2"
                    />
                    <v-icon
                      v-else
                      :icon="data.accountComboboxOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                      size="18"
                      class="combobox-chevron"
                      @mousedown.prevent="toggleCombobox"
                    />
                  </div>

                  <!-- Dropdown -->
                  <Transition name="dropdown">
                    <div v-if="data.accountComboboxOpen && !data.loadingAccounts" class="combobox-dropdown">
                      <div v-if="filteredAccounts.length === 0" class="combobox-empty">
                        Keine Accounts gefunden
                      </div>
                      <button
                        v-for="account in filteredAccounts"
                        :key="account.id"
                        class="combobox-option"
                        @mousedown.prevent="selectAccount(account)"
                      >
                        <div class="option-content">
                          <span class="option-name">{{ getAccountDisplayName(account) }}</span>
                          <span class="option-email">{{ account.email }}</span>
                        </div>
                        <span class="option-id">#{{ account.id }}</span>
                      </button>
                    </div>
                  </Transition>
                </div>
              </div>

              <!-- New Account Form -->
              <div v-if="data.accountMode === 'new'" class="new-account-section">
                <MigrationAccountForm @change="handleAccountFormChange" />
              </div>
            </section>

            <!-- Sub-Account Relationship (Optional) -->
            <section class="form-section">
              <h3 class="section-title">
                {{ $t('dashboard.admin.migration.import.subAccount.title') }}
                <span class="optional-badge">Optional</span>
              </h3>

              <label class="toggle-item" style="margin-bottom: 12px">
                <input v-model="data.enableSubAccount" type="checkbox" class="toggle-input" />
                <span class="toggle-switch"></span>
                <span class="toggle-label">{{ $t('dashboard.admin.migration.import.subAccount.enable') }}</span>
              </label>

              <template v-if="data.enableSubAccount">
                <p class="section-description">
                  {{ $t('dashboard.admin.migration.import.subAccount.description') }}
                </p>

                <!-- Parent Account Selection -->
                <div class="sub-account-fields">
                  <div class="field-group">
                    <label class="field-label">{{ $t('dashboard.admin.migration.import.subAccount.parentAccount') }}</label>

                    <!-- Selected Parent Display -->
                    <div v-if="data.parentAccount" class="selected-account">
                      <div class="account-info">
                        <span class="account-name">{{ getAccountDisplayName(data.parentAccount) }}</span>
                        <span class="account-email">{{ data.parentAccount.email }}</span>
                      </div>
                      <button class="remove-account" @click="clearParentAccount">
                        <v-icon icon="mdi-close" size="16" />
                      </button>
                    </div>

                    <!-- Parent Account Combobox -->
                    <div v-else class="combobox-container">
                      <div
                        class="combobox-input-wrapper"
                        :class="{ focused: data.parentComboboxOpen }"
                      >
                        <v-icon icon="mdi-account-search" size="18" class="combobox-icon" />
                        <input
                          v-model="data.parentComboboxQuery"
                          type="text"
                          class="combobox-input"
                          :placeholder="$t('dashboard.admin.migration.import.subAccount.searchParent')"
                          @focus="data.parentComboboxOpen = true"
                          @blur="handleParentComboboxBlur"
                        />
                        <v-icon
                          :icon="data.parentComboboxOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                          size="18"
                          class="combobox-chevron"
                          @mousedown.prevent="toggleParentCombobox"
                        />
                      </div>

                      <Transition name="dropdown">
                        <div v-if="data.parentComboboxOpen && !data.loadingAccounts" class="combobox-dropdown">
                          <div v-if="filteredParentAccounts.length === 0" class="combobox-empty">
                            {{ $t('dashboard.admin.migration.import.subAccount.noAccounts') }}
                          </div>
                          <button
                            v-for="account in filteredParentAccounts"
                            :key="account.id"
                            class="combobox-option"
                            @mousedown.prevent="selectParentAccount(account)"
                          >
                            <div class="option-content">
                              <span class="option-name">{{ getAccountDisplayName(account) }}</span>
                              <span class="option-email">{{ account.email }}</span>
                            </div>
                            <span class="option-id">#{{ account.id }}</span>
                          </button>
                        </div>
                      </Transition>
                    </div>
                  </div>

                  <!-- Relationship Type -->
                  <div class="field-group">
                    <label class="field-label">{{ $t('dashboard.admin.migration.import.subAccount.type') }}</label>
                    <div class="mode-toggle">
                      <label class="mode-option" :class="{ active: data.relationshipType === 'reseller' }">
                        <input v-model="data.relationshipType" type="radio" value="reseller" />
                        <span class="option-label">Reseller</span>
                      </label>
                      <label class="mode-option" :class="{ active: data.relationshipType === 'team' }">
                        <input v-model="data.relationshipType" type="radio" value="team" />
                        <span class="option-label">Team</span>
                      </label>
                    </div>
                  </div>

                  <!-- Billing Mode -->
                  <div class="field-group">
                    <label class="field-label">{{ $t('dashboard.admin.migration.import.subAccount.billingMode') }}</label>
                    <div class="mode-toggle">
                      <label class="mode-option" :class="{ active: data.billingMode === 'shared' }">
                        <input v-model="data.billingMode" type="radio" value="shared" />
                        <span class="option-label">{{ $t('dashboard.admin.migration.import.subAccount.billingShared') }}</span>
                      </label>
                      <label class="mode-option" :class="{ active: data.billingMode === 'independent' }">
                        <input v-model="data.billingMode" type="radio" value="independent" />
                        <span class="option-label">{{ $t('dashboard.admin.migration.import.subAccount.billingIndependent') }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </template>
            </section>

            <!-- Stripe Customer Linking (Optional) -->
            <section class="form-section">
              <h3 class="section-title">
                Stripe Kunde verknüpfen
                <span class="optional-badge">Optional</span>
              </h3>
              <p class="section-description">
                Verknüpfe einen bestehenden Stripe-Kunden mit dem Account für die Rechnungsstellung.
              </p>

              <!-- Selected Stripe Customer -->
              <div v-if="data.selectedStripeCustomer" class="selected-stripe-customer">
                <div class="stripe-customer-info">
                  <div class="stripe-icon">
                    <v-icon icon="mdi-credit-card" size="18" />
                  </div>
                  <div class="stripe-details">
                    <span class="stripe-name">{{ data.selectedStripeCustomer.name || 'Kein Name' }}</span>
                    <span class="stripe-email">{{ data.selectedStripeCustomer.email }}</span>
                    <span class="stripe-meta">
                      ID: {{ data.selectedStripeCustomer.id }} · Erstellt: {{ formatStripeDate(data.selectedStripeCustomer.created) }}
                    </span>
                  </div>
                </div>
                <button class="remove-stripe-customer" @click="clearSelectedStripeCustomer">
                  <v-icon icon="mdi-close" size="16" />
                </button>
              </div>

              <!-- Stripe Customer Search -->
              <div v-else class="stripe-search-container">
                <div class="search-box">
                  <v-icon icon="mdi-magnify" size="18" class="search-icon" />
                  <input
                    v-model="data.stripeSearch"
                    type="text"
                    class="search-input"
                    placeholder="Stripe-Kunden suchen (E-Mail oder Name)..."
                  />
                  <v-progress-circular
                    v-if="data.searchingStripe"
                    indeterminate
                    size="16"
                    width="2"
                    class="search-spinner"
                  />
                </div>

                <!-- Stripe Search Results -->
                <div v-if="data.stripeSearchResults.length > 0" class="stripe-search-results">
                  <button
                    v-for="customer in data.stripeSearchResults"
                    :key="customer.id"
                    class="stripe-result-item"
                    @click="selectStripeCustomer(customer)"
                  >
                    <div class="stripe-result-icon">
                      <v-icon icon="mdi-credit-card-outline" size="16" />
                    </div>
                    <div class="stripe-result-info">
                      <span class="stripe-result-name">{{ customer.name || 'Kein Name' }}</span>
                      <span class="stripe-result-email">{{ customer.email }}</span>
                    </div>
                    <span class="stripe-result-id">{{ customer.id }}</span>
                  </button>
                </div>

                <!-- No Results -->
                <div
                  v-else-if="data.stripeSearch.length >= 2 && !data.searchingStripe"
                  class="no-results"
                >
                  Keine Stripe-Kunden gefunden
                </div>
              </div>
            </section>

            <!-- Validation Section -->
            <section class="form-section validation-section">
              <h3 class="section-title">
                {{ $t('dashboard.admin.migration.validation.title') }}
              </h3>

              <!-- Validation Status -->
              <div v-if="data.validated" class="validation-status">
                <div v-if="data.allValid" class="status-badge status-valid">
                  <v-icon icon="mdi-check-circle" size="18" />
                  <span>{{ $t('dashboard.admin.migration.validation.allValid') }}</span>
                </div>
                <div v-else class="validation-results">
                  <div
                    v-for="result in data.validationResults"
                    :key="result.domain"
                    class="validation-item"
                    :class="{ invalid: !result.can_import }"
                  >
                    <span class="domain">{{ result.domain }}</span>
                    <span v-if="result.can_import" class="status valid">
                      <v-icon icon="mdi-check" size="16" />
                    </span>
                    <span v-else class="status invalid">
                      <v-icon icon="mdi-close" size="16" />
                      <span class="reason">{{ result.reason }}</span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Validation Button -->
              <button
                class="validate-btn"
                :disabled="!canValidate || data.validating"
                @click="validateImport"
              >
                <v-progress-circular
                  v-if="data.validating"
                  indeterminate
                  size="16"
                  width="2"
                />
                <v-icon v-else icon="mdi-check-decagram" size="18" />
                <span>
                  {{ data.validating
                    ? $t('dashboard.admin.migration.validation.validating')
                    : $t('dashboard.admin.migration.actions.validate')
                  }}
                </span>
              </button>
            </section>
          </div>

          <!-- Import Progress -->
          <div v-if="data.importing" class="import-progress">
            <div class="progress-header">
              <v-icon icon="mdi-import" size="20" class="pulse-icon" />
              <span class="progress-title">Import läuft...</span>
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar indeterminate"></div>
            </div>
            <p class="progress-text">{{ data.importProgress.currentDomain }}</p>
          </div>

          <!-- Footer -->
          <footer class="panel-footer">
            <button class="cancel-btn" :disabled="data.importing" @click="emit('close')">
              {{ $t('common.cancel') }}
            </button>
            <button
              class="import-btn"
              :disabled="!canImport || data.importing"
              @click="executeImport"
            >
              <v-progress-circular
                v-if="data.importing"
                indeterminate
                size="16"
                width="2"
              />
              <v-icon v-else icon="mdi-import" size="18" />
              <span>{{ data.importing ? 'Importiere...' : $t('dashboard.admin.migration.actions.import') }}</span>
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.import-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.import-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
  height: 100%;
  background: linear-gradient(135deg, rgba(15, 23, 48, 0.98), rgba(10, 15, 35, 0.98));
  border-left: 1px solid rgba(120, 163, 255, 0.15);
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.3);
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.panel-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.panel-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.6);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.1);
    border-color: rgba(244, 67, 54, 0.3);
    color: #e57373;
  }
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.form-section {
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  margin: 0 0 12px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(200, 218, 255, 0.5);
}

/* Domain Chips */
.domain-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.domain-chip {
  padding: 6px 12px;
  background: rgba(0, 150, 136, 0.1);
  border: 1px solid rgba(0, 150, 136, 0.2);
  border-radius: 16px;
  font-size: 0.85rem;
  color: #26a69a;
}

/* Mode Toggle */
.mode-toggle {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.mode-option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  input {
    display: none;
  }

  .option-label {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.7);
  }

  &.active {
    background: rgba(0, 150, 136, 0.1);
    border-color: rgba(0, 150, 136, 0.3);

    .option-label {
      color: #26a69a;
    }
  }

  &:hover:not(.active) {
    background: rgba(120, 163, 255, 0.05);
  }
}

/* Account Selection */
.account-search-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.new-account-section {
  min-width: 0;
  overflow: hidden;
}

/* Combobox */
.combobox-container {
  position: relative;
}

.combobox-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  transition: all 0.2s ease;

  &.focused {
    border-color: rgba(120, 163, 255, 0.3);
    background: rgba(15, 23, 48, 0.8);
  }
}

.combobox-icon {
  color: rgba(200, 218, 255, 0.4);
  flex-shrink: 0;
}

.combobox-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  min-width: 0;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }
}

.combobox-chevron {
  color: rgba(200, 218, 255, 0.4);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(200, 218, 255, 0.7);
  }
}

.combobox-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 280px;
  overflow-y: auto;
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(120, 163, 255, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(120, 163, 255, 0.5);
    }
  }
}

.combobox-empty {
  padding: 16px;
  text-align: center;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

.combobox-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 14px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  }
}

.option-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.option-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.option-email {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.option-id {
  font-size: 0.75rem;
  font-family: monospace;
  color: rgba(200, 218, 255, 0.3);
  flex-shrink: 0;
  margin-left: 12px;
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Import Progress */
.import-progress {
  padding: 16px 24px;
  background: rgba(0, 150, 136, 0.05);
  border-top: 1px solid rgba(0, 150, 136, 0.15);
}

.progress-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.pulse-icon {
  color: #26a69a;
  animation: pulse 1.5s ease-in-out infinite;
}

.progress-title {
  font-weight: 600;
  color: #26a69a;
}

.progress-bar-container {
  height: 4px;
  background: rgba(0, 150, 136, 0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #26a69a, #4db6ac);
  border-radius: 2px;
  transition: width 0.3s ease;

  &.indeterminate {
    width: 30%;
    animation: indeterminate 1.5s ease-in-out infinite;
  }
}

.progress-text {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes indeterminate {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(400%);
  }
}

.selected-account {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(0, 150, 136, 0.1);
  border: 1px solid rgba(0, 150, 136, 0.2);
  border-radius: 8px;
}

.account-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.account-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.account-email {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.remove-account {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #e57373;
  }
}

.search-container {
  position: relative;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: rgba(200, 218, 255, 0.4);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 40px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    border-color: rgba(120, 163, 255, 0.3);
    background: rgba(15, 23, 48, 0.8);
  }
}

.search-spinner {
  position: absolute;
  right: 12px;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin-top: 4px;
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.result-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.result-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.result-email {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.no-results {
  padding: 16px;
  text-align: center;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Stripe Customer Section */
.section-description {
  margin: 0 0 16px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  line-height: 1.5;
}

.optional-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: rgba(120, 163, 255, 0.1);
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: rgba(200, 218, 255, 0.5);
}

.stripe-search-container {
  position: relative;
}

.selected-stripe-customer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(99, 91, 255, 0.1);
  border: 1px solid rgba(99, 91, 255, 0.25);
  border-radius: 8px;
}

.stripe-customer-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.stripe-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(99, 91, 255, 0.15);
  border-radius: 8px;
  color: #7c74ff;
}

.stripe-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stripe-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.stripe-email {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.stripe-meta {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.4);
  font-family: monospace;
}

.remove-stripe-customer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #e57373;
  }
}

.stripe-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin-top: 4px;
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(99, 91, 255, 0.2);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.stripe-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(99, 91, 255, 0.1);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.stripe-result-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(99, 91, 255, 0.1);
  border-radius: 6px;
  color: #7c74ff;
}

.stripe-result-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.stripe-result-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.stripe-result-email {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.stripe-result-id {
  font-size: 0.7rem;
  font-family: monospace;
  color: rgba(200, 218, 255, 0.3);
}

/* Validation */
.validation-section {
  padding-top: 20px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.validation-status {
  margin-bottom: 16px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.9rem;

  &.status-valid {
    background: rgba(76, 175, 80, 0.1);
    border: 1px solid rgba(76, 175, 80, 0.2);
    color: #81c784;
  }
}

.validation-results {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.validation-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 8px;

  &.invalid {
    background: rgba(244, 67, 54, 0.05);
    border-color: rgba(244, 67, 54, 0.2);
  }

  .domain {
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }

  .status {
    display: flex;
    align-items: center;
    gap: 6px;

    &.valid {
      color: #81c784;
    }

    &.invalid {
      color: #e57373;
    }

    .reason {
      font-size: 0.8rem;
    }
  }
}

.validate-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.25);
  border-radius: 8px;
  color: #42a5f5;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(33, 150, 243, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Footer */
.panel-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.cancel-btn,
.import-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cancel-btn {
  background: transparent;
  border: 1px solid rgba(120, 163, 255, 0.2);
  color: rgba(200, 218, 255, 0.7);

  &:hover {
    background: rgba(120, 163, 255, 0.05);
  }
}

.import-btn {
  background: rgba(0, 150, 136, 0.15);
  border: 1px solid rgba(0, 150, 136, 0.3);
  color: #26a69a;

  &:hover:not(:disabled) {
    background: rgba(0, 150, 136, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Slide Animation */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;

  .import-panel {
    transform: translateX(100%);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .import-panel {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 255, 0.98));
  }

  .panel-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .panel-subtitle {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Sub-Account Toggle & Fields */
.toggle-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-input {
  display: none;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 12px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: rgba(200, 218, 255, 0.5);
    border-radius: 50%;
    transition: all 0.2s ease;
  }
}

.toggle-input:checked + .toggle-switch {
  background: rgba(0, 150, 136, 0.2);
  border-color: rgba(0, 150, 136, 0.4);

  &::after {
    left: 22px;
    background: #26a69a;
  }
}

.toggle-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.sub-account-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(200, 218, 255, 0.4);
}

/* Responsive */
@media (max-width: 520px) {
  .import-panel {
    max-width: 100%;
  }
}
</style>
